import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, Save, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ActionsEditor } from '../components/ActionsEditor';
import { RunList, StepList, TraceView } from '../components/automation-runs';
import { ChipSelect, ConditionBuilder, useConditionOptions, type ConditionOptions } from '../components/ConditionBuilder';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  ConfirmDialog,
  ErrorState,
  Field,
  Input,
  PageHeader,
  PageLoader,
  Select,
  Switch,
  Textarea,
} from '../components/ui';
import { WorkOrderStatusBadge } from '../components/work-order-badges';
import { api, errorMessage } from '../lib/api';
import {
  defaultAction,
  DURATION_UNIT_LABEL,
  emptyGroup,
  TIME_TRIGGERS,
  TRIGGER_DESCRIPTION,
  TRIGGER_LABEL,
  UPDATE_FIELD_LABEL,
  useWorkflows,
} from '../lib/automation';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { useDebouncedValue } from '../lib/hooks';
import type {
  AutomationRun,
  ConditionGroup,
  DurationUnit,
  Page,
  Workflow,
  WorkflowAction,
  WorkflowTestResult,
  WorkflowTrigger,
  WorkOrder,
} from '../lib/types';

interface Draft {
  name: string;
  description: string;
  enabled: boolean;
  trigger: WorkflowTrigger;
  amount: number;
  unit: DurationUnit;
  businessHours: boolean;
  fields: string[];
  conditions: ConditionGroup;
  actions: WorkflowAction[];
  stopProcessing: boolean;
}

const TRIGGERS = Object.keys(TRIGGER_LABEL) as WorkflowTrigger[];

const blankDraft = (): Draft => ({
  name: '',
  description: '',
  enabled: false,
  trigger: 'TICKET_CREATED',
  amount: 4,
  unit: 'hours',
  businessHours: true,
  fields: [],
  conditions: emptyGroup(),
  actions: [defaultAction('assign')],
  stopProcessing: false,
});

function toDraft(workflow: Workflow): Draft {
  return {
    name: workflow.name,
    description: workflow.description ?? '',
    enabled: workflow.enabled,
    trigger: workflow.trigger,
    amount: workflow.triggerConfig.amount ?? 4,
    unit: workflow.triggerConfig.unit ?? 'hours',
    businessHours: workflow.triggerConfig.businessHours ?? true,
    fields: workflow.triggerConfig.fields ?? [],
    conditions: workflow.conditions,
    actions: workflow.actions,
    stopProcessing: workflow.stopProcessing,
  };
}

function toBody(draft: Draft) {
  const triggerConfig = TIME_TRIGGERS.includes(draft.trigger)
    ? { amount: draft.amount, unit: draft.unit, businessHours: draft.businessHours }
    : draft.trigger === 'TICKET_UPDATED'
      ? { fields: draft.fields }
      : {};
  return {
    name: draft.name,
    description: draft.description,
    enabled: draft.enabled,
    trigger: draft.trigger,
    triggerConfig,
    conditions: draft.conditions,
    actions: draft.actions,
    stopProcessing: draft.stopProcessing,
  };
}

const updateFieldOptions = Object.entries(UPDATE_FIELD_LABEL).map(([value, label]) => ({ value, label }));

function TestPanel({ draft, options }: { draft: Draft; options: ConditionOptions }) {
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search.trim(), 250);
  const [ticket, setTicket] = useState<WorkOrder | null>(null);
  const [changes, setChanges] = useState<string[]>(['status']);
  const [commentBy, setCommentBy] = useState<'REQUESTER' | 'STAFF'>('REQUESTER');
  const [commentInternal, setCommentInternal] = useState(false);

  const tickets = useQuery({
    queryKey: ['work-orders', 'workflow-test', debounced],
    queryFn: () => api.get<Page<WorkOrder>>('/work-orders', { state: 'all', search: debounced || undefined, pageSize: 6 }),
    placeholderData: keepPreviousData,
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<WorkflowTestResult>('/workflows/test', {
        workflow: toBody(draft),
        workOrderId: ticket!.id,
        event: draft.trigger === 'TICKET_UPDATED' ? { changes } : draft.trigger === 'COMMENT_ADDED' ? { commentBy, commentInternal } : {},
      }),
  });
  const result = test.data;

  return (
    <Card>
      <CardHeader title="Test against a ticket" description="See what this workflow would do to a real ticket. Nothing is changed." />
      <div className="space-y-4 p-5">
        <div>
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tickets by #, summary, or asset tag" className="pl-8" />
          </div>
          <ul className="mt-2 max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
            {(tickets.data?.items ?? []).map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    setTicket(item);
                    test.reset();
                  }}
                  className={cn('flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50', ticket?.id === item.id && 'bg-brand-50')}
                >
                  <span className="min-w-0 flex-1 truncate">
                    <span className="text-slate-400">#{item.number}</span> {item.title}
                  </span>
                  <WorkOrderStatusBadge status={item.status} />
                </button>
              </li>
            ))}
            {tickets.data?.items.length === 0 && <li className="px-3 py-2 text-sm text-slate-500">No tickets match</li>}
          </ul>
        </div>

        {draft.trigger === 'TICKET_UPDATED' && (
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Pretend these changed</span>
            <ChipSelect options={updateFieldOptions} value={changes} onChange={setChanges} placeholder="Add a change" />
          </div>
        )}
        {draft.trigger === 'COMMENT_ADDED' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Pretend a comment from">
              <Select value={commentBy} onChange={(e) => setCommentBy(e.target.value as 'REQUESTER' | 'STAFF')}>
                <option value="REQUESTER">The requester</option>
                <option value="STAFF">The technology team</option>
              </Select>
            </Field>
            <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={commentInternal} onChange={(e) => setCommentInternal(e.target.checked)} className="size-4 rounded border-slate-300" />
              Internal note
            </label>
          </div>
        )}

        <Button variant="primary" icon={<FlaskConical />} disabled={!ticket} loading={test.isPending} onClick={() => test.mutate()} className="w-full">
          {ticket ? `Test on #${ticket.number}` : 'Choose a ticket to test'}
        </Button>
        {test.error && <Alert tone="red">{errorMessage(test.error)}</Alert>}

        {result && (
          <div className="space-y-4 border-t border-slate-100 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              {result.matched ? <Badge tone="green">Matches</Badge> : <Badge tone="gray">Doesn’t match</Badge>}
              <span className="text-sm text-slate-600">
                #{result.ticket.number} {result.ticket.title}
              </span>
            </div>
            {result.timing && (
              <Alert tone={result.timing.due ? 'amber' : 'blue'}>
                {!result.timing.eligible
                  ? draft.trigger === 'UNANSWERED'
                    ? 'This ticket has been answered or closed, so this trigger won’t fire for it.'
                    : 'This ticket is closed, so this trigger won’t fire for it.'
                  : result.timing.due
                    ? `The timer is up (since ${formatDateTime(result.timing.dueAt)}), so this would run on the next check.`
                    : `The timer started ${formatDateTime(result.timing.since)} and is up ${formatDateTime(result.timing.dueAt)}.`}
              </Alert>
            )}
            <div>
              <p className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">Conditions</p>
              <TraceView node={result.trace} options={options} />
            </div>
            {result.matched && (
              <div>
                <p className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">It would</p>
                {result.steps.length ? <StepList steps={result.steps} /> : <p className="text-sm text-slate-500">Do nothing: there are no actions.</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export function WorkflowEditorPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const options = useConditionOptions();
  const workflows = useWorkflows();
  const [draft, setDraft] = useState<Draft | null>(isNew ? blankDraft() : null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const query = useQuery({ queryKey: ['workflow', id], queryFn: () => api.get<Workflow>(`/workflows/${id}`), enabled: !isNew });
  const runs = useQuery({
    queryKey: ['automation-runs', 'workflow', id],
    queryFn: () => api.get<Page<AutomationRun>>('/automation/runs', { workflowId: id, pageSize: 15 }),
    enabled: !isNew,
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (query.data && !dirty) setDraft(toDraft(query.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  const update = (patch: Partial<Draft>) => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: () => (isNew ? api.post<Workflow>('/workflows', toBody(draft!)) : api.patch<Workflow>(`/workflows/${id}`, toBody(draft!))),
    onSuccess: (saved) => {
      setError('');
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.setQueryData(['workflow', saved.id], saved);
      toast.success('Workflow saved');
      if (isNew) navigate(`/automation/workflows/${saved.id}`, { replace: true });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/workflows/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
      toast.success('Workflow deleted');
      navigate('/automation');
    },
    onError: (err) => {
      setDeleting(false);
      toast.error(errorMessage(err));
    },
  });

  if (!isNew && query.isLoading) return <PageLoader />;
  if (!isNew && query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (!draft) return <PageLoader />;

  const timeTrigger = TIME_TRIGGERS.includes(draft.trigger);
  const otherWorkflows = (workflows.data ?? []).filter((w) => w.id !== id);

  return (
    <>
      <PageHeader
        back={{ to: '/automation', label: 'Automation' }}
        title={draft.name || (isNew ? 'New workflow' : 'Untitled workflow')}
        meta={
          <>
            {draft.enabled ? <Badge tone="green">On</Badge> : <Badge>Off</Badge>}
            {dirty && <Badge tone="amber">Unsaved changes</Badge>}
          </>
        }
        actions={
          canEdit && (
            <>
              {!isNew && (
                <Button variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              )}
              <Button variant="primary" icon={<Save />} loading={save.isPending} disabled={!isNew && !dirty} onClick={() => save.mutate()}>
                Save
              </Button>
            </>
          )
        }
      />
      {!canEdit && (
        <Alert tone="blue" className="mb-6">
          Only administrators can change workflows. You can still test this one against a ticket.
        </Alert>
      )}
      {error && (
        <Alert tone="red" className="mb-6">
          {error}
        </Alert>
      )}

      <div className="grid gap-6 xl:grid-cols-3">
        <fieldset disabled={!canEdit} className="min-w-0 space-y-6 xl:col-span-2">
          <Card>
            <div className="grid gap-4 p-5 sm:grid-cols-[1fr_auto]">
              <Field label="Name" required>
                <Input value={draft.name} maxLength={120} onChange={(e) => update({ name: e.target.value })} placeholder="e.g. Route Wi-Fi problems to Wireless" />
              </Field>
              <div className="sm:mt-6">
                <Switch checked={draft.enabled} onChange={(enabled) => update({ enabled })} label="Turned on" />
              </div>
              <Field label="Description" className="sm:col-span-2">
                <Textarea rows={2} maxLength={1000} value={draft.description} onChange={(e) => update({ description: e.target.value })} placeholder="What this workflow is for" />
              </Field>
            </div>
          </Card>

          <Card>
            <CardHeader title="1. When" description="What starts this workflow" />
            <div className="space-y-4 p-5">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {TRIGGERS.map((trigger) => (
                  <button
                    key={trigger}
                    type="button"
                    onClick={() => update({ trigger })}
                    className={cn(
                      'rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed',
                      draft.trigger === trigger ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500' : 'border-slate-200 hover:border-slate-300',
                    )}
                  >
                    <p className="text-sm font-medium text-slate-900">{TRIGGER_LABEL[trigger]}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{TRIGGER_DESCRIPTION[trigger]}</p>
                  </button>
                ))}
              </div>
              {timeTrigger && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                  <span>After</span>
                  <Input
                    type="number"
                    min={1}
                    max={10000}
                    value={String(draft.amount)}
                    onChange={(e) => update({ amount: Number(e.target.value) || 1 })}
                    className="w-20"
                    aria-label="Amount"
                  />
                  <Select value={draft.unit} onChange={(e) => update({ unit: e.target.value as DurationUnit })} className="w-auto" aria-label="Unit">
                    {(Object.keys(DURATION_UNIT_LABEL) as DurationUnit[]).map((unit) => (
                      <option key={unit} value={unit}>
                        {DURATION_UNIT_LABEL[unit]}
                      </option>
                    ))}
                  </Select>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={draft.businessHours} onChange={(e) => update({ businessHours: e.target.checked })} className="size-4 rounded border-slate-300" />
                    counting business hours only
                  </label>
                </div>
              )}
              {draft.trigger === 'TICKET_UPDATED' && (
                <div>
                  <span className="mb-1 block text-sm font-medium text-slate-700">Only when these change</span>
                  <ChipSelect options={updateFieldOptions} value={draft.fields} onChange={(fields) => update({ fields })} placeholder="Any change" />
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="2. If" description="Conditions the ticket must meet. Use groups to mix AND and OR." />
            <div className="p-5">
              <ConditionBuilder value={draft.conditions} onChange={(conditions) => update({ conditions })} trigger={draft.trigger} options={options} />
            </div>
          </Card>

          <Card>
            <CardHeader title="3. Then" description="Actions run in order. A wait pauses before the next step." />
            <div className="space-y-4 p-5">
              <ActionsEditor actions={draft.actions} onChange={(actions) => update({ actions })} options={options} workflows={otherWorkflows} />
              <label className="flex items-start gap-2 border-t border-slate-100 pt-4 text-sm text-slate-700">
                <input type="checkbox" checked={draft.stopProcessing} onChange={(e) => update({ stopProcessing: e.target.checked })} className="mt-0.5 size-4 rounded border-slate-300" />
                <span>
                  Stop here when this matches
                  <span className="block text-xs text-slate-500">Workflows later in the list won’t run for the same event.</span>
                </span>
              </label>
            </div>
          </Card>
        </fieldset>

        <div className="min-w-0 space-y-6">
          <TestPanel draft={draft} options={options} />
          {!isNew && (
            <Card>
              <CardHeader title="Recent runs" description="Updates every 15 seconds" />
              {runs.data?.items.length ? (
                <RunList runs={runs.data.items} options={options} showName={false} />
              ) : (
                <p className="px-5 py-4 text-sm text-slate-500">{runs.isLoading ? 'Loading…' : 'This workflow hasn’t run yet.'}</p>
              )}
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title={`Delete “${draft.name}”?`}
        description="Its run history is kept. Waiting runs are canceled."
        confirmLabel="Delete workflow"
      />
    </>
  );
}
